import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  UserDataService,
  UserProfileService,
  UserTripHistoryService,
} from '../firebase/userProfile';
import type {
  UserTripHistory,
  UpdateUserProfileRequest,
  UpdateTravelPreferencesRequest,
  TravelPreferences,
} from '../../shared/types/user';

// ===== QUERY KEYS =====

const QUERY_KEYS = {
  userProfile: (userId: string) => ['userProfile', userId],
  userTripHistory: (userId: string) => ['userTripHistory', userId],
  completeUserData: (userId: string) => ['completeUserData', userId],
} as const;

// ===== USER PROFILE HOOKS =====

export function useUserProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.userProfile(user?.uid || ''),
    queryFn: () => UserProfileService.getUserProfile(user!.uid),
    enabled: !!user?.uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const updateMutation = useMutation({
    mutationFn: (updates: UpdateUserProfileRequest) =>
      UserProfileService.updateUserProfile(user!.uid, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userProfile(user!.uid),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.completeUserData(user!.uid),
      });
    },
  });

  const updateTravelPreferencesMutation = useMutation({
    mutationFn: (preferences: UpdateTravelPreferencesRequest) =>
      UserProfileService.updateTravelPreferences(user!.uid, preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userProfile(user!.uid),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.completeUserData(user!.uid),
      });
      // Update profile completion status
      UserDataService.updateProfileCompletion(user!.uid);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => UserProfileService.deleteUserProfile(user!.uid),
    onSuccess: () => {
      queryClient.clear();
    },
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    updateProfile: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    updateTravelPreferences: updateTravelPreferencesMutation.mutate,
    isUpdatingTravelPreferences: updateTravelPreferencesMutation.isPending,
    updateTravelPreferencesError: updateTravelPreferencesMutation.error,
    deleteProfile: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
    refetch: query.refetch,
  };
}

// ===== USER TRIP HISTORY HOOKS =====

export function useUserTripHistory(limit: number = 10) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.userTripHistory(user?.uid || ''),
    queryFn: () => UserTripHistoryService.getUserTripHistory(user!.uid, limit),
    enabled: !!user?.uid,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const addMutation = useMutation({
    mutationFn: (tripHistory: UserTripHistory) =>
      UserTripHistoryService.addTripToHistory(tripHistory),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userTripHistory(user!.uid),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.completeUserData(user!.uid),
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      tripId,
      updates,
    }: {
      tripId: string;
      updates: Partial<UserTripHistory>;
    }) =>
      UserTripHistoryService.updateTripInHistory(user!.uid, tripId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userTripHistory(user!.uid),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.completeUserData(user!.uid),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tripId: string) =>
      UserTripHistoryService.deleteTripFromHistory(user!.uid, tripId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userTripHistory(user!.uid),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.completeUserData(user!.uid),
      });
    },
  });

  return {
    tripHistory: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    addTrip: addMutation.mutate,
    isAdding: addMutation.isPending,
    addError: addMutation.error,
    updateTrip: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    deleteTrip: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    deleteError: deleteMutation.error,
    refetch: query.refetch,
  };
}

// ===== COMPLETE USER DATA HOOKS =====

export function useCompleteUserData() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEYS.completeUserData(user?.uid || ''),
    queryFn: () => UserDataService.getCompleteUserData(user!.uid),
    enabled: !!user?.uid,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const initializeMutation = useMutation({
    mutationFn: ({
      email,
      displayName,
      travelPreferences,
    }: {
      email: string;
      displayName: string;
      travelPreferences?: Partial<TravelPreferences>;
    }) =>
      UserDataService.initializeUserData(
        user!.uid,
        email,
        displayName,
        travelPreferences
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.completeUserData(user!.uid),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.userProfile(user!.uid),
      });
    },
  });

  return {
    userData: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    initializeUserData: initializeMutation.mutate,
    isInitializing: initializeMutation.isPending,
    initializeError: initializeMutation.error,
    refetch: query.refetch,
  };
}

// ===== PROFILE COMPLETION HOOKS =====

export function useProfileCompletion() {
  const { profile } = useUserProfile();

  const calculateCompletionStatus = () => {
    if (!profile) {
      return {
        basicInfo: false,
        completionPercentage: 0,
      };
    }

    const basicInfo = !!(profile.displayName && profile.email);

    return {
      basicInfo,
      completionPercentage: basicInfo ? 100 : 0,
    };
  };

  const completionStatus = calculateCompletionStatus();

  const nextSteps: string[] = [];
  if (!completionStatus.basicInfo) {
    nextSteps.push('Complete basic profile information');
  }

  return {
    completionStatus,
    nextSteps,
    isComplete: completionStatus.completionPercentage === 100,
  };
}

// ===== PROFILE SETUP CHECK HOOKS =====

export function useIsProfileSetup() {
  const { profile, isLoading } = useUserProfile();

  const isSetup =
    !isLoading &&
    profile &&
    profile.profileCompletionStatus.basicInfo &&
    profile.profileCompletionStatus.preferences;

  return {
    isProfileSetup: isSetup,
    isLoading,
  };
}

// ===== TRAVEL PREFERENCES UTILITY HOOK =====

export function useTravelPreferences() {
  const { profile } = useUserProfile();

  return {
    travelPreferences: profile?.travelPreferences,
    hasTravelPreferences: !!profile?.travelPreferences,
  };
}
